import { CHINESE_NEW_YEARS, SEASONS } from './definitions';
import { compareSeasonWeekKeys } from './seasonWeekOrder';
import { Settlement } from './types';
import { startOfWeek, getWeek } from 'date-fns';
import { es } from 'date-fns/locale';

export function capitalize(str?: string): string {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

/**
 * Formatea un número usando el formato español/latinoamericano
 * Punto (.) para separador de miles
 * Coma (,) para separador decimal
 * @param value - El número a formatear
 * @param decimals - Número de decimales (default: 0 para enteros)
 * @returns String formateado (ej: 1.234,56)
 */
export function formatNumber(
  value: number | null | undefined,
  decimals: number = 0
): string {
  if (value == null || isNaN(value)) return '0';

  return value.toLocaleString('es-ES', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

export function isValidExcelDate(value: any): boolean {
  // Excel dates are numbers greater than 1 (1 = January 1, 1900)
  return typeof value === 'number' && value > 1;
}

export function convertExcelDate(excelDate: any): string | null {
  try {
    if (isValidExcelDate(excelDate)) {
      // Convert Excel date to JS date
      // Excel dates are number of days since 1900-01-01
      // 25569 is the number of days between 1900-01-01 and 1970-01-01
      const date = new Date((excelDate - 25569) * 86400 * 1000);
      return date.toISOString();
    } else if (typeof excelDate === 'string') {
      // Try parsing as regular date string
      const date = new Date(excelDate);
      if (!isNaN(date.getTime())) {
        return date.toISOString();
      }
    }
    return null;
  } catch (error) {
    console.error('Error converting date:', error);
    return null;
  }
}

export const getCurrentSeasonChinese = (date: Date) => {
  const year = date.getFullYear();
  if (date < CHINESE_NEW_YEARS[`season${year}`]) {
    return `${year - 1}-${year}`;
  }
  return `${year}-${year + 1}`;
};

export const calculatePerformance = (settlement: Settlement) => {
  const quartileGroups = settlement.settlement_details?.reduce(
    (acc, detail) => {
      const quartile = detail.quartil_vs_market;
      const kilos = detail.kilos || 0;

      if (quartile === null) {
        if (!acc['null']) {
          acc['null'] = 0;
        }
        acc['null'] += kilos;
      } else {
        if (!acc[quartile]) {
          acc[quartile] = 0;
        }
        acc[quartile] += kilos;
      }

      return acc;
    },
    {} as Record<string | number, number>
  );

  const totalKilos = Object.values(quartileGroups || {}).reduce(
    (sum, kilos) => sum + kilos,
    0
  );

  // Calculate percentage for each quartile separately
  const nullsPercentage = ((quartileGroups?.['null'] || 0) / totalKilos) * 100;
  const q1Percentage = ((quartileGroups?.[1] || 0) / totalKilos) * 100;
  const q2Percentage = ((quartileGroups?.[2] || 0) / totalKilos) * 100;
  const q3Percentage = ((quartileGroups?.[3] || 0) / totalKilos) * 100;
  const q4Percentage = ((quartileGroups?.[4] || 0) / totalKilos) * 100;

  return {
    q1: Math.round(q1Percentage),
    q2: Math.round(q2Percentage),
    q3: Math.round(q3Percentage),
    q4: Math.round(q4Percentage),
    nulls: Math.round(nullsPercentage),
  };
};

export const calculateMedian = (prices: number[]): number => {
  const sorted = [...prices].sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);

  if (sorted.length % 2 === 0) {
    return (sorted[middle - 1] + sorted[middle]) / 2;
  }
  return sorted[middle];
};

export const getColorClass = (value: number) => {
  if (value === null || value === undefined) return '';
  if (value > 0) return 'text-green-600';
  if (value < 0) return 'text-red-600';
};

export const getColor = (value: number) => {
  if (value === null || value === undefined) return '';
  if (value > 0) return 'green';
  if (value < 0) return 'red';
};

export const sortWeekKeys = (a: string, b: string): number => {
  const getYearAndWeek = (key: string) => {
    const match = key.match(/(\d{4})-W(\d{1,2})/);
    if (!match) return { year: 0, week: 0 };
    return {
      year: parseInt(match[1]),
      week: parseInt(match[2]),
    };
  };

  const keyA = getYearAndWeek(a);
  const keyB = getYearAndWeek(b);

  // Si las semanas están en diferentes años
  if (keyA.year !== keyB.year) {
    // Para semanas cercanas al cambio de año (ej: W51 2024 vs W1 2025)
    if (Math.abs(keyA.week - keyB.week) > 40) {
      // Si la diferencia es grande, la semana mayor (ej: W51) debe ir primero
      return keyB.week - keyA.week;
    }
    return keyA.year - keyB.year;
  }

  // Si es el mismo año, ordenar normalmente
  return keyA.week - keyB.week;
};

export const getWeekKey = (date: string | Date) => {
  try {
    if (date == null) return null;
    // If date is a Date object, convert to ISO string first
    const dateStr = date instanceof Date ? date.toISOString() : date;
    let dateObj = dateStr;
    if (dateStr.includes('T')) {
      dateObj = dateStr.replace(/T\d{2}:\d{2}:\d{2}/, 'T12:00:00');
    } else {
      dateObj = dateStr + 'T12:00:00';
    }

    const dateInstance = new Date(dateObj);
    const weekNumber = getWeek(dateInstance, {
      weekStartsOn: 1, // Monday
      firstWeekContainsDate: 4, // This follows ISO 8601 standard
      locale: es,
    });

    // Get the year of the Thursday of this week
    // This ensures correct year assignment for ISO weeks
    const thursdayDate = new Date(dateInstance);
    thursdayDate.setDate(
      dateInstance.getDate() + (4 - (dateInstance.getDay() || 7))
    );
    const weekYear = thursdayDate.getFullYear();
    // console.log('cheee', weekYear, weekNumber);
    return `${weekYear}-W${weekNumber}`;
  } catch (error) {
    console.error('Error in getWeekKey:', error, 'for date:', date);
    return 'Invalid Week';
  }
};

/** Número de semana ISO (1–53) de una fecha, o null si no se puede calcular. */
export const getWeekNumber = (date: string | Date): number | null => {
  const key = getWeekKey(date);
  const m = key ? String(key).match(/W(\d{1,2})$/) : null;
  return m ? parseInt(m[1], 10) : null;
};

export const getWeekStart = (date: Date) => {
  return startOfWeek(date, {
    weekStartsOn: 1, // Lunes
    locale: es,
  });
};

export const calculateWeightedAverage = (
  items: Array<{ kilos: number; value: number | null }>,
  getValue: (item: { kilos: number; value: number | null }) => number | null
) => {
  const validItems = items.filter(
    (item) => item.kilos > 0 && getValue(item) != null
  );

  const totalKilos = validItems.reduce((sum, item) => sum + item.kilos, 0);

  if (totalKilos === 0) return 0;

  return (
    validItems.reduce((sum, item) => sum + getValue(item)! * item.kilos, 0) /
    totalKilos
  );
};

export function groupSettlementsByShip(
  settlements: Settlement[],
  getDate?: (settlement: Settlement) => string,
  options?: { includeMarketInAir?: boolean }
): Record<string, Settlement[]> {
  const byShips: Record<string, Settlement[]> = {};
  const includeMarket = options?.includeMarketInAir ?? false;

  settlements.forEach((settlement) => {
    let shipKey: string;
    const date = getDate ? getDate(settlement) : settlement.date_sold;

    if (settlement.ship_trip?.ship?.type == 'A') {
      const weekPart = `AIR-${getWeekKey(date)}`;
      shipKey = includeMarket
        ? `${weekPart}-M${settlement.market_id || 'UNKNOWN'}`
        : weekPart;
    } else if (settlement.ship_trip?.ship?.type == 'T') {
      shipKey = `GROUND-${getWeekKey(date)}`;
    } else {
      shipKey = `SHIP-${settlement.ship_trip.ship.name
        .trim()
        .replace(/\s+/g, '_')}-${settlement.ship_trip.eta}`;
    }

    if (!byShips[shipKey]) {
      byShips[shipKey] = [];
    }
    byShips[shipKey].push(settlement);
  });

  return byShips;
}

export function groupSettlementsByWeek(
  settlements: Settlement[],
  getDate?: (settlement: Settlement) => string
): Record<string, Settlement[]> {
  const byWeeks: Record<string, Settlement[]> = {};

  settlements.forEach((settlement) => {
    const date = getDate ? getDate(settlement) : settlement.date_sold;
    const weekKey = `WEEK-${getWeekKey(date)}`;

    if (!byWeeks[weekKey]) {
      byWeeks[weekKey] = [];
    }
    byWeeks[weekKey].push(settlement);
  });

  return byWeeks;
}

interface ClientSaldo {
  positive: number;
  negative: number;
  total: number;
}

interface ClientVarietyFOB {
  client: string;
  variety: string;
  caliber: string;
  color: string;
  totalKilos: number;
  totalFob: number;
  fobPerKg: number;
}

export function calculateClientSaldos(
  groupedSettlements: Record<string, Settlement[]>,
  includeFormat: boolean = false
): Record<string, ClientSaldo> {
  const clientSaldos: Record<string, ClientSaldo> = {};

  // Procesar cada nave por separado
  Object.entries(groupedSettlements).forEach(([shipKey, shipSettlements]) => {
    // Agrupar detalles por cliente dentro de la nave
    const clientDetails: Record<
      string,
      Record<string, { kilos: number; fob: number }>
    > = {};

    // 1. Recolectar todos los detalles por cliente para esta nave
    shipSettlements.forEach((settlement) => {
      const client = settlement.receiver?.trim() || 'Sin Cliente';
      if (!clientDetails[client]) {
        clientDetails[client] = {};
      }

      settlement.settlement_details?.forEach((detail) => {
        if (!detail.variety || !detail.caliber || !detail.color) return;

        const detailKey = includeFormat
          ? `${detail.variety}-${detail.caliber}-${detail.color}-${detail.format}`
          : `${detail.variety}-${detail.caliber}-${detail.color}`;

        if (!clientDetails[client][detailKey]) {
          clientDetails[client][detailKey] = {
            kilos: 0,
            fob: 0,
          };
        }

        // Sumar kilos y FOB y redondear en cada suma
        clientDetails[client][detailKey].kilos = Number(
          (clientDetails[client][detailKey].kilos + detail.kilos).toFixed(2)
        );
        clientDetails[client][detailKey].fob = Number(
          (clientDetails[client][detailKey].fob + detail.usd_total_fob).toFixed(
            2
          )
        );
      });
    });

    // 2. Calcular diferencias para cada cliente y detalle dentro de la nave
    Object.entries(clientDetails).forEach(([client, details]) => {
      if (!clientDetails[client]) {
        clientDetails[client] = {};
      }

      if (!clientSaldos[client]) {
        clientSaldos[client] = {
          positive: 0,
          negative: 0,
          total: 0,
        };
      }

      // Para cada detalle del cliente principal
      Object.entries(details).forEach(([detailKey, mainDetail]) => {
        // Solo procesar si hay kilos
        if (mainDetail.kilos > 0) {
          // Calcular FOB/KG del cliente principal y redondear
          const mainFobPerKg = Number(
            (mainDetail.fob / mainDetail.kilos).toFixed(2)
          );

          // Calcular FOB/KG promedio de los otros clientes en la misma nave
          let othersTotalKilos = 0;
          let othersTotalFob = 0;

          // Sumar los kilos y FOB de los otros clientes para este detalle
          Object.entries(clientDetails).forEach(
            ([otherClient, otherDetails]) => {
              if (otherClient !== client && otherDetails[detailKey]) {
                // Redondear en cada suma
                othersTotalKilos = Number(
                  (othersTotalKilos + otherDetails[detailKey].kilos).toFixed(2)
                );
                othersTotalFob = Number(
                  (othersTotalFob + otherDetails[detailKey].fob).toFixed(2)
                );
              }
            }
          );

          // Solo calcular diferencias si hay datos para comparar
          if (othersTotalKilos > 0) {
            // Calcular FOB/KG promedio de comparación y redondear
            const othersFobPerKg = Number(
              (othersTotalFob / othersTotalKilos).toFixed(2)
            );

            // Calcular diferencia por kilo y redondear
            const diffFobPerKg = Number(
              (mainFobPerKg - othersFobPerKg).toFixed(2)
            );

            // Calcular diferencia total y redondear
            const totalDifference = Number(
              (diffFobPerKg * mainDetail.kilos).toFixed(2)
            );

            // Actualizar saldos
            if (totalDifference > 0) {
              clientSaldos[client].positive = Number(
                (clientSaldos[client].positive + totalDifference).toFixed(2)
              );
            } else {
              clientSaldos[client].negative = Number(
                (clientSaldos[client].negative + totalDifference).toFixed(2)
              );
            }

            // Actualizar el total sumando positivos y negativos
            clientSaldos[client].total = Number(
              (
                clientSaldos[client].positive + clientSaldos[client].negative
              ).toFixed(2)
            );
          }
        }
      });
    });
  });

  return clientSaldos;
}

export const getSettlementIncoterm = (settlement: Settlement) => {
  if (
    !settlement.total_freight_usd &&
    !settlement.total_commission_local &&
    !settlement.total_other_costs_local &&
    !settlement.total_repacking_costs_local &&
    !settlement.total_tax_local
  ) {
    return 'pre_liquidacion';
  } else if (!settlement.total_freight_usd || !settlement.exchange_rate) {
    return 'cif';
  }
  return 'fob';
};

export function inferSeason(dateSold: string): string {
  const date = new Date(dateSold);

  for (const [_, season] of Object.entries(SEASONS)) {
    if (date >= season.start && date <= season.end) {
      return season.name;
    }
  }
  return '';
}

export const getCurrentSeason = () => {
  const now = new Date();
  // Find the current season from SEASONS
  return (
    Object.values(SEASONS).find(
      (season) => now >= season.start && now <= season.end
    )?.name || Object.values(SEASONS)[Object.values(SEASONS).length - 1].name
  );
};

export const normalizeSeriesData = (series: any[]) => {
  // Extract all unique x values across all series
  const allXValues = new Set<string>();
  series.forEach((serie) => {
    serie.data.forEach((point) => {
      allXValues.add(point.x);
    });
  });

  // Orden de temporada: 27→53 y luego 1→26
  const sortedXValues = Array.from(allXValues).sort((a, b) => {
    if (typeof a === 'number') {
      return (a as number) - (b as number);
    }

    return compareSeasonWeekKeys(a as string, b as string);
  });

  // Normalize each series to include all x values
  return series.map((serie) => {
    const pointMap = new Map(serie.data.map((point) => [point.x, point.y]));
    const normalizedData = sortedXValues.map((x) => ({
      x,
      y: pointMap.has(x) ? pointMap.get(x) : null,
    }));

    return {
      ...serie,
      data: normalizedData,
    };
  });
};

export const getColorInCherryFormat = (color: string) => {
  try {
    const trimmedColor = color.trim().toUpperCase();
    if (
      trimmedColor === 'DARK DARK' ||
      trimmedColor === 'DARKDARK' ||
      trimmedColor == 'DD'
    )
      return 'DD';
    if (
      trimmedColor === 'DARK' ||
      trimmedColor === 'D' ||
      trimmedColor == 'DARK'
    )
      return 'D';
    if (trimmedColor === 'LIGHT' || trimmedColor === 'L') return 'L';

    return trimmedColor;
  } catch (error) {
    console.error(
      'Error in getColorInCherryFormat:',
      error,
      'for color:',
      color
    );
    return 'NO ENCONTRADO';
  }
};

export const getShipTypeInCherryFormat = (shipType: string) => {
  try {
    let trimmedShipType = shipType?.trim().toUpperCase();
    if (trimmedShipType === 'MARITIMO') return 'M';
    if (trimmedShipType === 'TERRESTRE') return 'T';
    if (trimmedShipType === 'AEREO') return 'A';

    return 'M';
  } catch (error) {
    console.error(
      'Error in getShipTypeInCherryFormat:',
      error,
      'for shipType:',
      shipType
    );
    return 'M';
  }
};
